"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { Wrench, ShieldCheck, Clock, CircleCheck as CheckCircle2, CircleAlert as AlertCircle, FileText, PhoneCall, Calendar, Activity, ArrowRight, Sparkles, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
const servicePlans = [{
  id: "essential",
  name: "Essential Care",
  tagline: "Standard compliance & preventative maintenance",
  popular: false,
  frequency: "Annual",
  badgeText: "Standard Tier",
  turnaround: "48-72h On-site Response",
  f1: "1x Annual Preventative Maintenance visit",
  f2: "Standard calibration & certification documentation",
  f3: "OEM diagnostic check & wear-part assessment",
  f4: "Standard business hours technical phone support",
  f5: "10% discount on non-scheduled replacement parts",
  ctaLabel: "Select Essential Plan"
}, {
  id: "comprehensive",
  name: "Complete Assurance",
  tagline: "Priority servicing for high-throughput regulated labs",
  popular: true,
  frequency: "Bi-Annual / Quarterly",
  badgeText: "Recommended",
  turnaround: "24h Priority On-site Response",
  f1: "2x Annual PM visits & full sensor recalibrations",
  f2: "IQ/OQ/PQ re-qualification protocols & audit-ready certificates",
  f3: "Emergency breakdown labor and travel included",
  f4: "Dedicated regional field service engineer",
  f5: "Priority dispatch for genuine factory OEM spares",
  ctaLabel: "Select Complete Plan"
}, {
  id: "mission-critical",
  name: "Priority Manufacturing Support Plan",
  tagline: "Zero-downtime coverage for continuous line operations",
  popular: false,
  frequency: "Custom Scheduled",
  badgeText: "Enterprise Tier",
  turnaround: "Guaranteed 8-12h Rapid Dispatch",
  f1: "Quarterly preventative overhaul & continuous diagnostics",
  f2: "Comprehensive validation and audit support",
  f3: "On-site critical spare parts consignment kit",
  f4: "Emergency hotline with remote engineering triage (availability confirmed at contract)",
  f5: "Loaner instrument availability during extended workshop repairs",
  ctaLabel: "Request Custom Plan"
}];
const serviceIntervals = [{
  schedule: "Every 3 Months",
  focus: "Optics Cleaning & Verification",
  scope: "Light source verification, purge integrity check, baseline diagnostic scan, and air filter replacements.",
  regulation: "Good Laboratory Practice (GLP) standard verification"
}, {
  schedule: "Every 6 Months",
  focus: "Intermediate Mechanical & Sensor Audit",
  scope: "Internal drive belt calibration, motor torque testing, fluidic line leak tests, and temperature sensor drift verification.",
  regulation: "Periodic intermediate performance checks"
}, {
  schedule: "Every 12 Months",
  focus: "Full Preventative Maintenance (PM) & OQ",
  scope: "Complete optical realignment, rebuild of high-wear assemblies, electronic safety check, and formal Operational Qualification reverification.",
  regulation: "GMP/FDA annual compliance standards"
}, {
  schedule: "Every 24-36 Months",
  focus: "Major Component Overhaul",
  scope: "Laser diode / detector head replacement, deep electronic bus diagnostics, board-level firmware overhaul, and complete recertification.",
  regulation: "Full lifecycle extension validation"
}];
const repairCapabilities = [{
  title: "Rapid Emergency Field Response",
  description: "Factory-trained field service engineers based across Ireland and the UK ready for urgent fault diagnosis and rapid part replacement.",
  icon: "zap"
}, {
  title: "In-House Workshop Depot Diagnostics",
  description: "State-of-the-art cleanroom bench facilities equipped for intricate component-level repairs, board diagnostics, and optic alignment.",
  icon: "wrench"
}, {
  title: "Genuine OEM Certified Spares",
  description: "We stock authentic factory parts with full batch traceability and certificate of conformity to protect instrument warranty and compliance.",
  icon: "shield"
}, {
  title: "Regulatory Re-Qualification After Repair",
  description: "Every repair follows strict change control protocols with post-service IQ/OQ testing to return equipment directly to productive GMP status.",
  icon: "file"
}];
const faqs = [{
  q: "How quickly can an engineer attend an unexpected instrument breakdown?",
  a: "Under our Complete Assurance and Manufacturing Shield contracts, our average on-site arrival is within 24 hours across Ireland and the UK. Emergency remote triage begins within 60 minutes of call logging."
}, {
  q: "Are your calibration and repair services audit-ready for FDA/HPRA inspections?",
  a: "Yes. All maintenance, calibrations, and repairs are executed strictly under documented SOPs aligned with recognized quality and testing standards, complete with traceable equipment and signed qualification protocols."
}, {
  q: "Can Antech Solutions service legacy or multi-vendor instrumentation?",
  a: "Our senior engineering team possesses extensive expertise across major analytical platforms, PAT sensors, and powder testing systems. We can tailor multi-vendor service contracts to consolidate your vendor management."
}, {
  q: "What happens if an instrument requires off-site workshop repair?",
  a: "For complex overhauls requiring specialized bench testing, we organize secure transport to our technical facility. For contracted clients, we provide temporary loaner units to avoid lab disruption."
}];
export default function Maintenance() {
  const [activeTab, setActiveTab] = useState("plans");
  const prefersReducedMotion = useReducedMotion();
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "zap":
        return <Zap className="size-5 text-primary" />;
      case "wrench":
        return <Wrench className="size-5 text-primary" />;
      case "shield":
        return <ShieldCheck className="size-5 text-primary" />;
      case "file":
        return <FileText className="size-5 text-primary" />;
      default:
        return <Activity className="size-5 text-primary" />;
    }
  };
  return <section id="maintenanceandrepairs" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container">
        {/* Header Block */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4 px-3 py-1 font-medium text-xs">
            <Sparkles className="size-3.5 text-primary mr-1" />
            Lifecycle Engineering & Support
          </Badge>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground font-default mb-4">
            Maintenance Programs & Repair Services
          </h2>
          <p className="text-muted-foreground text-base md:text-lg leading-relaxed font-sans">
            Ensure continuous uptime, measurement precision, and stringent regulatory compliance across regulated laboratories and manufacturing facilities in Ireland and the UK.
          </p>
        </div>

        {/* Navigation Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full mb-12">
          <div className="flex justify-center mb-8">
            <TabsList className="bg-secondary/80 p-1 rounded-xl h-auto flex-wrap justify-center border border-border">
              <TabsTrigger value="plans" className="px-5 py-2.5 text-sm font-medium rounded-lg transition-all">
                <ShieldCheck className="size-4 mr-2" />
                Service Programs
              </TabsTrigger>
              <TabsTrigger value="intervals" className="px-5 py-2.5 text-sm font-medium rounded-lg transition-all">
                <SafeHydrate><Calendar className="size-4 mr-2" /></SafeHydrate>
                Service Intervals
              </TabsTrigger>
              <TabsTrigger value="repairs" className="px-5 py-2.5 text-sm font-medium rounded-lg transition-all">
                <Wrench className="size-4 mr-2" />
                Repair Capabilities
              </TabsTrigger>
              <TabsTrigger value="faq" className="px-5 py-2.5 text-sm font-medium rounded-lg transition-all">
                <AlertCircle className="size-4 mr-2" />
                Support FAQ
              </TabsTrigger>
            </TabsList>
          </div>

          {/* TAB 1: Service Plans */}
          <TabsContent value="plans" className="focus-visible:outline-none">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
              {servicePlans.map((plan, i) => <div key={plan.id} data-index={i} className="flex">
                  <motion.div initial={prefersReducedMotion ? false : {
                opacity: 0,
                y: 15
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.35,
                delay: i * 0.1
              }} className="w-full flex">
                    <Card className={`relative flex flex-col justify-between w-full rounded-xl border transition-all duration-200 ${plan.popular ? "border-primary shadow-md bg-card ring-1 ring-primary/20" : "border-border shadow-xs bg-card hover:border-primary/40"}`}>
                      {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <Badge className="bg-primary text-primary-foreground font-semibold px-3 py-0.5 shadow-sm">
                            Most Selected
                          </Badge>
                        </div>}

                      <div>
                        <CardHeader className="pb-4">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <Badge variant={plan.popular ? "default" : "secondary"}>
                              {plan.badgeText}
                            </Badge>
                            <span className="text-xs font-semibold text-muted-foreground flex items-center gap-1">
                              <Clock className="size-3.5 text-primary" />
                              {plan.frequency}
                            </span>
                          </div>
                          <CardTitle className="text-xl font-bold text-foreground">
                            {plan.name}
                          </CardTitle>
                          <CardDescription className="text-xs leading-relaxed mt-1">
                            {plan.tagline}
                          </CardDescription>
                        </CardHeader>

                        <CardContent className="space-y-4 pt-0">
                          <div className="p-3 bg-secondary/50 rounded-lg border border-border/60">
                            <span className="text-xs font-medium text-muted-foreground block">
                              Service Level Agreement
                            </span>
                            <span className="text-sm font-semibold text-foreground flex items-center gap-1.5 mt-0.5">
                              <Activity className="size-4 text-primary" />
                              {plan.turnaround}
                            </span>
                          </div>

                          <Separator />

                          <ul className="space-y-2.5 text-sm text-foreground">
                            <li className="flex items-start gap-2">
                              <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                              <span className="text-xs leading-tight">{plan.f1}</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                              <span className="text-xs leading-tight">{plan.f2}</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                              <span className="text-xs leading-tight">{plan.f3}</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                              <span className="text-xs leading-tight">{plan.f4}</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                              <span className="text-xs leading-tight">{plan.f5}</span>
                            </li>
                          </ul>
                        </CardContent>
                      </div>

                      <CardFooter className="pt-2">
                        <Button asChild variant={plan.popular ? "default" : "outline"} className="w-full font-medium">
                          <Link to="/ContactPage">
                            {plan.ctaLabel}
                            <ArrowRight className="size-4 ml-1.5" />
                          </Link>
                        </Button>
                      </CardFooter>
                    </Card>
                  </motion.div>
                </div>)}
            </div>
          </TabsContent>

          {/* TAB 2: Service Intervals */}
          <TabsContent value="intervals" className="focus-visible:outline-none">
            <Card className="border border-border shadow-xs bg-card">
              <CardHeader>
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div>
                    <CardTitle className="text-xl font-bold">
                      Recommended Preventive Service Schedule
                    </CardTitle>
                    <CardDescription className="mt-1">
                      Proactive service routines designed to eliminate unplanned downtime and preserve analytical precision.
                    </CardDescription>
                  </div>
                  <Button asChild variant="outline" size="sm" className="w-fit">
                    <Link to="/ServicesPage">
                      View All Services
                    </Link>
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <Table>
                  <TableHeader>
                    <TableRow className="hover:bg-transparent">
                      <TableHead className="w-[180px] font-bold text-foreground">Interval</TableHead>
                      <TableHead className="w-[240px] font-bold text-foreground">Service Focus</TableHead>
                      <TableHead className="font-bold text-foreground">Key Technical Scope</TableHead>
                      <TableHead className="w-[240px] font-bold text-foreground">Compliance Target</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {serviceIntervals.map((row, i) => <TableRow key={i} data-index={i} className="hover:bg-secondary/40">
                        <TableCell className="font-semibold text-primary">
                          <span className="inline-flex items-center gap-1.5">
                            <Clock className="size-3.5" />
                            {row.schedule}
                          </span>
                        </TableCell>
                        <TableCell className="font-medium text-foreground">
                          {row.focus}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-xs leading-relaxed whitespace-normal">
                          {row.scope}
                        </TableCell>
                        <TableCell className="text-xs">
                          <Badge variant="secondary" className="font-normal text-[11px]">
                            {row.regulation}
                          </Badge>
                        </TableCell>
                      </TableRow>)}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* TAB 3: Repair Capabilities */}
          <TabsContent value="repairs" className="focus-visible:outline-none">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {repairCapabilities.map((item, i) => <div key={i} data-index={i}>
                  <Card className="h-full border border-border shadow-xs hover:border-primary/40 transition-colors bg-card">
                    <CardContent className="pt-6">
                      <div className="flex items-start gap-4">
                        <div className="size-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 border border-primary/20">
                          {getIcon(item.icon)}
                        </div>
                        <div>
                          <h3 className="text-base font-bold text-foreground mb-1.5">
                            {item.title}
                          </h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>)}
            </div>

            <div className="mt-8 bg-secondary/60 rounded-xl p-6 border border-border flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="size-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 shadow-xs">
                  <PhoneCall className="size-6" />
                </div>
                <div>
                  <h4 className="text-base font-bold text-foreground">
                    Experiencing an Immediate Instrument Fault?
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Direct technical assistance and rapid dispatch for urgent breakdown situations.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-3 w-full md:w-auto">
                <Button asChild variant="default" className="w-full md:w-auto">
                  <Link to="/ContactPage">
                    Request Emergency Engineer
                  </Link>
                </Button>
              </div>
            </div>
          </TabsContent>

          {/* TAB 4: Support FAQ */}
          <TabsContent value="faq" className="focus-visible:outline-none">
            <Card className="border border-border shadow-xs bg-card max-w-3xl mx-auto">
              <CardHeader>
                <CardTitle className="text-xl font-bold">
                  Frequently Asked Support Questions
                </CardTitle>
                <CardDescription>
                  Clear information on our service procedures, standards, and engineering coverage.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <Accordion type="single" collapsible className="w-full">
                  {faqs.map((faq, i) => <AccordionItem key={i} value={`item-${i}`} data-index={i}>
                      <AccordionTrigger className="text-left font-semibold text-foreground text-sm hover:no-underline">
                        {faq.q}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-sm leading-relaxed">
                        {faq.a}
                      </AccordionContent>
                    </AccordionItem>)}
                </Accordion>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Bottom Banner */}
        <div className="mt-12 text-center bg-secondary/40 rounded-xl p-6 border border-border max-w-4xl mx-auto">
          <p className="text-sm text-muted-foreground">
            Looking for tailored compliance or multi-vendor qualification documentation?{" "}
            <Link to="/ContactPage" className="text-primary font-semibold hover:underline">
              Speak with our field engineering team
            </Link>{" "}
            to schedule an initial instrument audit.
          </p>
        </div>
      </div>
    </section>;
}